import type { ColumnDef } from '@tanstack/react-table'
import { Printer } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { RouterInputs, RouterOutputs } from 'shared/api'
import { trpcClient } from 'shared/api'
import { DataTable } from 'shared/components/data-table/data-table'
import { ScanInput } from 'shared/components/scan-input'
import { Badge } from 'shared/components/ui/badge'
import { Button } from 'shared/components/ui/button'
import { Input } from 'shared/components/ui/input'
import { Label } from 'shared/components/ui/label'
import { useDataTable } from 'shared/hooks/use-data-table'
import { useTranslate } from 'shared/lib/i18n'
import { cn } from 'shared/lib/utils'
import { useAuthStore } from 'shared/store/auth'
import { formatDate, formatDayTime } from 'shared/lib/utils/date'
import { SectionTitle } from 'shared/components/section'
import { ToneBanner } from 'shared/components/tone-banner'

/**
 * «Возврат партнёру» — a Parcel goes back to its partner only from the hub (ADR-0037,
 * spec §18, §22; ticket 42). The hub worker picks the partner and sees its shelf
 * «вернуть партнёру»; scans each Parcel handed over into the handover (an outbound
 * Manifest); prints the act — partner, date, hub, the codes, how many, «принял
 * (ФИО)», signatures (the act goes to paper only; the screen shows the handover in a
 * line); types the name of who received it and closes. Only the close makes the Parcels
 * «Возвращена партнёру»; what was not scanned stays on the shelf. The act prints
 * again from the list of closed handovers. Support and the admin work it too, at the
 * hub they pick.
 *
 * Every call is injected so the behaviour is tested without a backend.
 */
export type PartnerShelf = RouterOutputs['returnFlow']['PartnerShelves'][number]
export type HandoverAct = RouterOutputs['returnFlow']['Handover']
export type HandoverScanAnswer = RouterOutputs['returnFlow']['HandoverScan']
export type HandoverScanRequest = RouterInputs['returnFlow']['HandoverScan']
export type HandoverCloseRequest = RouterInputs['returnFlow']['HandoverClose']
export type HandoverCloseAnswer = RouterOutputs['returnFlow']['HandoverClose']

export interface HubPartnerReturnProps {
	listShelves?: (hubId?: string) => Promise<PartnerShelf[]>
	readHandover?: (handoverId: string) => Promise<HandoverAct>
	listClosed?: (hubId?: string) => Promise<HandoverAct[]>
	scan?: (input: HandoverScanRequest) => Promise<HandoverScanAnswer>
	close?: (input: HandoverCloseRequest) => Promise<HandoverCloseAnswer>
	listHubs?: () => Promise<{ hubId: string; id: string }[]>
	print?: () => void
}

const shelvesViaTrpc = (hubId?: string) => trpcClient.returnFlow.PartnerShelves.query(hubId ? { hubId } : {})
const handoverViaTrpc = (handoverId: string) => trpcClient.returnFlow.Handover.query({ handoverId })
const closedViaTrpc = (hubId?: string) => trpcClient.returnFlow.ClosedHandovers.query(hubId ? { hubId } : {})
const scanViaTrpc = (input: HandoverScanRequest) => trpcClient.returnFlow.HandoverScan.mutate(input)
const closeViaTrpc = (input: HandoverCloseRequest) => trpcClient.returnFlow.HandoverClose.mutate(input)
const hubsViaTrpc = async () => (await trpcClient.trips.Options.query()).hubs
const printViaBrowser = () => window.print()

const failure = (error: unknown) => (error instanceof Error ? error.message : String(error))

export function HubPartnerReturn({
	listShelves = shelvesViaTrpc,
	readHandover = handoverViaTrpc,
	listClosed = closedViaTrpc,
	scan = scanViaTrpc,
	close = closeViaTrpc,
	listHubs = hubsViaTrpc,
	print = printViaBrowser,
}: HubPartnerReturnProps) {
	const { t } = useTranslate()
	const role = useAuthStore((s) => s.user?.role)
	const picksHub = role !== undefined && role !== 'hub'
	const [hubs, setHubs] = useState<{ hubId: string; id: string }[]>([])
	const [hubId, setHubId] = useState<string | undefined>(undefined)
	const [shelves, setShelves] = useState<PartnerShelf[] | null>(null)
	const [closed, setClosed] = useState<HandoverAct[]>([])
	const [partnerId, setPartnerId] = useState<string | null>(null)
	const [act, setAct] = useState<HandoverAct | null>(null)
	const [answer, setAnswer] = useState<HandoverScanAnswer | null>(null)
	const [round, setRound] = useState(0)
	const [receiver, setReceiver] = useState('')
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [printing, setPrinting] = useState<{ act: HandoverAct; justClosed: boolean } | null>(null)

	useEffect(() => {
		if (!picksHub) return
		listHubs().then((list) => {
			setHubs(list)
			setHubId((current) => current ?? list[0]?.hubId)
		}, (e) => setError(failure(e)))
	}, [picksHub, listHubs])

	const ready = !picksHub || hubId !== undefined
	const refresh = useCallback(() => {
		if (!ready) return
		listShelves(hubId).then(setShelves, (e) => setError(failure(e)))
		listClosed(hubId).then(setClosed, (e) => setError(failure(e)))
	}, [ready, hubId, listShelves, listClosed])
	useEffect(() => refresh(), [refresh])

	const shelf = shelves?.find((s) => s.partnerId === partnerId) ?? null

	// The partner's open handover, when one is being built: its act is read to show it.
	useEffect(() => {
		setAct(null)
		if (shelf?.openHandoverId) readHandover(shelf.openHandoverId).then(setAct, (e) => setError(failure(e)))
	}, [shelf?.openHandoverId, readHandover])

	const onScan = async (code: string) => {
		if (!partnerId || busy) return
		setBusy(true)
		setError(null)
		try {
			const res = await scan({ partnerId, code, ...(hubId ? { hubId } : {}) })
			setAnswer(res)
			if (res.outcome !== 'refused') setAct(res.handover)
			setRound((n) => n + 1)
			refresh()
		} catch (e) {
			setError(failure(e))
		} finally {
			setBusy(false)
		}
	}

	const onClose = async () => {
		if (!act || busy || receiver.trim() === '') return
		setBusy(true)
		setError(null)
		try {
			const res = await close({ handoverId: act.handoverId, receiverName: receiver.trim() })
			if (res.outcome === 'refused') setError(t(`returnRefusal.${res.reason}`))
			else {
				setPrinting({ act: res.handover, justClosed: true })
				setAct(null)
				setReceiver('')
				setAnswer(null)
				refresh()
			}
		} catch (e) {
			setError(failure(e))
		} finally {
			setBusy(false)
		}
	}

	const inHandover = shelf?.parcels.filter((p) => p.inHandover).length ?? 0

	return (
		<div className='flex flex-col gap-6' data-testid='hub-partner-return'>
			<div className='flex flex-wrap items-center gap-3'>
				{shelves === null ? (
					<p className='text-sm text-muted-foreground'>{t('partnerReturn.loading')}</p>
				) : shelves.length === 0 ? (
					<p className='text-sm text-muted-foreground' data-testid='partner-return-empty'>
						{t('partnerReturn.empty')}
					</p>
				) : (
					<>
						<span className='text-sm font-semibold'>{t('partnerReturn.shelvesTitle')}</span>
						<div className='inline-flex flex-wrap gap-1 rounded-lg bg-muted p-1'>
							{shelves.map((s) => (
								<button
									key={s.partnerId}
									type='button'
									aria-pressed={s.partnerId === partnerId}
									className={cn(
										'inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
										s.partnerId === partnerId
											? 'bg-background text-foreground shadow-sm'
											: 'text-muted-foreground hover:text-foreground',
									)}
									data-testid={`partner-return-partner-${s.partnerId}`}
									onClick={() => {
										setPartnerId(s.partnerId)
										setAnswer(null)
									}}
								>
									{s.partnerName}
									<span className='text-xs tabular-nums text-muted-foreground'>{s.parcels.length}</span>
								</button>
							))}
						</div>
					</>
				)}
				{picksHub && hubs.length > 1 && (
					<select
						className='ml-auto h-8 w-fit rounded-lg border border-input bg-transparent px-2.5 text-sm'
						data-testid='hub-partner-return-hub'
						aria-label={t('hubTrips.hub')}
						value={hubId ?? ''}
						onChange={(e) => setHubId(e.target.value)}
					>
						{hubs.map((h) => (
							<option key={h.hubId} value={h.hubId}>
								{h.id}
							</option>
						))}
					</select>
				)}
			</div>
			{error && (
				<p role='alert' className='text-sm text-destructive' data-testid='hub-partner-return-error'>
					{error}
				</p>
			)}

			{shelf && (
				<section className='flex flex-col gap-4' data-testid='partner-return-shelf'>
					<h2 className='text-lg font-semibold'>{t('partnerReturn.shelfOf', { partner: shelf.partnerName })}</h2>
					<ScanInput key={round} onScan={onScan} disabled={busy} />
					{answer ? (
						<ToneBanner
							tone={answer.tone}
							title={
								answer.outcome === 'refused'
									? t('hubFromDriver.refused')
									: t(`partnerReturn.${answer.outcome === 'added' ? 'added' : 'alreadyIn'}`)
							}
							detail={
								answer.outcome === 'refused'
									? `${answer.trackingCode}: ${t(`returnRefusal.${answer.reason}`)}`
									: answer.trackingCode
							}
							testId='partner-return-answer'
							attributes={{ 'data-outcome': answer.outcome }}
						/>
					) : (
						<p className='-mt-1 text-xs text-muted-foreground'>{t('partnerReturn.scanHint')}</p>
					)}

					{act && act.state === 'BUILDING' && (
						<div
							className='flex flex-wrap items-end gap-3 rounded-lg border bg-muted/30 px-4 py-3'
							data-testid='partner-return-open'
						>
							<div className='flex flex-col gap-0.5'>
								<span className='text-sm font-semibold'>{t('partnerReturn.openTitle')}</span>
								<span className='text-sm text-muted-foreground'>
									{t('partnerReturn.count', { count: String(act.count) })}
								</span>
							</div>
							<Button variant='outline' data-testid='partner-return-print' onClick={print}>
								<Printer />
								{t('partnerReturn.printAct')}
							</Button>
							<div className='ml-auto flex items-end gap-2'>
								<div className='grid w-64 gap-1.5'>
									<Label htmlFor='partner-return-receiver'>{t('partnerReturn.receiver')}</Label>
									<Input
										id='partner-return-receiver'
										data-testid='partner-return-receiver'
										value={receiver}
										onChange={(e) => setReceiver(e.target.value)}
									/>
								</div>
								<Button
									data-testid='partner-return-close'
									disabled={busy || receiver.trim() === ''}
									onClick={onClose}
								>
									{t('partnerReturn.close')}
								</Button>
							</div>
							<div className='hidden print:block'>
								<HandoverActSheet act={act} />
							</div>
						</div>
					)}

					<ShelfTable parcels={shelf.parcels} inHandover={inHandover} />
				</section>
			)}

			{printing && (
				<div
					className='flex flex-wrap items-center gap-3 rounded-lg border border-emerald-500/60 bg-emerald-500/10 px-4 py-3 text-emerald-800 dark:text-emerald-300'
					data-testid='partner-return-closed-act'
				>
					<p className='text-sm font-medium' role='status'>
						{printing.justClosed
							? t('partnerReturn.closedNotice', { count: String(printing.act.count) })
							: t('partnerReturn.actOf', {
									partner: printing.act.partnerName,
									date: printing.act.handedOverAt ? formatDayTime(printing.act.handedOverAt) : '',
								})}
					</p>
					<Button variant='outline' className='ml-auto bg-background' data-testid='partner-return-reprint' onClick={print}>
						<Printer />
						{t('partnerReturn.printAct')}
					</Button>
					<div className='hidden print:block'>
						<HandoverActSheet act={printing.act} />
					</div>
				</div>
			)}

			{closed.length > 0 && <History closed={closed} onAct={(h) => setPrinting({ act: h, justClosed: false })} />}
		</div>
	)
}

type ShelfParcel = PartnerShelf['parcels'][number]

/** The partner's shelf: each Parcel, why it goes back, and whether it is in the handover now. */
function ShelfTable({ parcels, inHandover }: { parcels: ShelfParcel[]; inHandover: number }) {
	const { t } = useTranslate()
	const columns = useMemo<ColumnDef<ShelfParcel>[]>(
		() => [
			{ id: 'code', header: t('hub.shelfCode'), cell: ({ row }) => <span className='font-mono'>{row.original.trackingCode}</span> },
			{
				id: 'reasons',
				header: t('hub.shelfReasons'),
				cell: ({ row }) => (
					<span className='text-muted-foreground'>
						{row.original.reasons.map((r) => t(`hubShelfReason.${r}`)).join(' · ')}
					</span>
				),
			},
			{
				id: 'state',
				header: t('hub.colState'),
				meta: { cellClassName: 'w-px' },
				cell: ({ row }) =>
					row.original.inHandover ? (
						<Badge>{t('partnerReturn.inHandover')}</Badge>
					) : (
						<Badge variant='secondary'>{t('partnerReturn.onShelf')}</Badge>
					),
			},
		],
		[t],
	)
	const { table } = useDataTable({
		data: parcels,
		columns,
		rowCount: parcels.length,
		manualPagination: false,
		manualSorting: false,
		manualFiltering: false,
		enableRowSelection: false,
		getRowId: (p) => p.parcelId,
		initialState: { pagination: { pageIndex: 0, pageSize: 1000 } },
	})
	return (
		<div className='flex flex-col gap-2'>
			<SectionTitle
				title={t('partnerReturn.shelfTitle')}
				count={parcels.length}
				aside={
					inHandover > 0 && (
						<span className='text-sm text-muted-foreground'>
							{t('partnerReturn.inHandoverCount', { count: String(inHandover) })}
						</span>
					)
				}
			/>
			<DataTable
				table={table}
				withPagination={false}
				maxHeightClassName='max-h-[480px]'
				row={{
					attributes: (p) => ({
						'data-testid': `partner-return-parcel-${p.parcelId}`,
						'data-in-handover': String(p.inHandover),
					}),
				}}
			/>
		</div>
	)
}

/** The handovers closed before, each with its act to print again. */
function History({ closed, onAct }: { closed: HandoverAct[]; onAct: (act: HandoverAct) => void }) {
	const { t } = useTranslate()
	const columns = useMemo<ColumnDef<HandoverAct>[]>(
		() => [
			{ id: 'partner', header: t('hub.shelfPartner'), cell: ({ row }) => row.original.partnerName },
			{
				id: 'at',
				header: t('partnerReturn.colHandedOver'),
				cell: ({ row }) => (
					<span className='text-muted-foreground'>
						{row.original.handedOverAt ? formatDayTime(row.original.handedOverAt) : '—'}
					</span>
				),
			},
			{ id: 'receiver', header: t('partnerReturn.colReceiver'), cell: ({ row }) => row.original.receiverName ?? '—' },
			{
				id: 'count',
				header: t('hubSorting.colParcels'),
				meta: { headClassName: 'text-right', cellClassName: 'text-right tabular-nums' },
				cell: ({ row }) => row.original.count,
			},
			{
				id: 'actions',
				header: '',
				meta: { cellClassName: 'w-px text-right' },
				cell: ({ row }) => (
					<Button size='sm' variant='ghost' onClick={() => onAct(row.original)}>
						<Printer />
						{t('partnerReturn.actAgain')}
					</Button>
				),
			},
		],
		[t, onAct],
	)
	const { table } = useDataTable({
		data: closed,
		columns,
		rowCount: closed.length,
		manualPagination: false,
		manualSorting: false,
		manualFiltering: false,
		enableRowSelection: false,
		getRowId: (h) => h.handoverId,
		initialState: { pagination: { pageIndex: 0, pageSize: 1000 } },
	})
	return (
		<section className='flex flex-col gap-2' data-testid='partner-return-history'>
			<SectionTitle title={t('partnerReturn.historyTitle')} count={closed.length} />
			<DataTable
				table={table}
				withPagination={false}
				maxHeightClassName='max-h-[400px]'
				row={{ attributes: (h) => ({ 'data-testid': `partner-return-history-${h.handoverId}` }) }}
			/>
		</section>
	)
}

/** The act of a handover to the partner, as it goes to paper (spec §22 Ф6). */
export function HandoverActSheet({ act }: { act: HandoverAct }) {
	const { t } = useTranslate()
	const date = formatDate(act.handedOverAt ?? act.createdAt)
	return (
		<div data-print-label data-testid='handover-act' className='flex flex-col gap-2 bg-white p-4 text-sm text-black'>
			<div className='text-lg font-bold'>{t('partnerReturn.actTitle')}</div>
			<div data-testid='handover-act-partner'>{t('partnerReturn.actPartner', { partner: act.partnerName })}</div>
			<div data-testid='handover-act-hub'>{t('partnerReturn.actHub', { hub: act.hubCode })}</div>
			<div data-testid='handover-act-date'>{t('partnerReturn.actDate', { date })}</div>
			<ol className='list-decimal pl-6 font-mono' data-testid='handover-act-lines'>
				{act.lines.map((l) => (
					<li key={l.parcelId}>{l.trackingCode}</li>
				))}
			</ol>
			<div data-testid='handover-act-count'>{t('partnerReturn.actCount', { count: String(act.count) })}</div>
			<div data-testid='handover-act-receiver'>
				{t('partnerReturn.actReceiver', { name: act.receiverName ?? '______________________' })}
			</div>
			<div className='mt-4 grid grid-cols-2 gap-6'>
				<div>{t('partnerReturn.actSignHub')} ______________</div>
				<div>{t('partnerReturn.actSignPartner')} ______________</div>
			</div>
		</div>
	)
}
